/**
 * Web research on a matter — `POST`/`GET /api/matters/{id}/research`.
 *
 * The plan's web-evidence lane (P2 item 19) shipped its prompt and never its
 * fetch. This is the fetch, as a tool an attorney reaches for rather than a
 * pipeline stage: one URL and an optional search go in, Firecrawl reads the
 * page, the `judge` model says whether it is about the right party and what
 * it establishes, and the answer lands on the matter's own timeline.
 *
 * Three rules travel with every row, and the card renders all three:
 *
 * - **A page is data, never instruction.** `injection_flag` is set when a page
 *   addressed the model, and a flagged page is rejected whatever it claimed.
 * - **A fact about our own client is a question for them.** `whose === 'ours'`
 *   never becomes a case fact; the card labels it "to confirm with the client".
 * - **A fact that cuts against us is recorded as it is.** `cuts` is shown in
 *   the model's own words; nothing here softens `against_us`.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'

export type ResearchWhose = 'cited' | 'ours'
export type ResearchVerdict = 'accept' | 'reject'
export type ResearchCuts = 'for_us' | 'against_us' | 'neutral'
export type ResearchStrength = 'strong' | 'fair' | 'weak'
export type ResearchConfidence = 'certain' | 'likely' | 'unsure'
export type ResearchFactType =
  | 'use'
  | 'non_use'
  | 'goods'
  | 'channel'
  | 'proprietor_status'
  | 'reputation'
  | 'other'

/** One judged page, exactly as the server records it. */
export interface ResearchCuration {
  matter_id: string
  /** The `research.curated` event on the matter's timeline. */
  event_id: number
  url: string
  title: string
  whose: ResearchWhose
  /** The search that found the page, if the attorney gave one. */
  query: string
  /** The opening of the page, for the reviewer's eyes beside the verdict. */
  excerpt: string
  /** Which model judged it — `provider/model`, never a person. */
  model_id: string
  verdict: ResearchVerdict
  identity_confidence: ResearchConfidence
  fact_type: ResearchFactType
  /** Empty whenever the verdict is `reject`. */
  fact: string
  supports: string
  cuts: ResearchCuts
  dated: string
  strength: ResearchStrength
  reason: string
  injection_flag: boolean
}

export interface ResearchInput {
  url: string
  whose: ResearchWhose
  query: string
}

export const researchKeys = {
  list: (matterId: string) => ['matters', matterId, 'research'] as const,
}

export function useMatterResearch(matterId: string | null): UseQueryResult<ResearchCuration[]> {
  return useQuery<ResearchCuration[]>({
    queryKey: researchKeys.list(matterId ?? ''),
    queryFn: () =>
      api.get<ResearchCuration[]>(`/api/matters/${encodeURIComponent(matterId ?? '')}/research`),
    enabled: Boolean(matterId),
  })
}

export function useResearchUrl(matterId: string) {
  const client = useQueryClient()
  return useMutation<ResearchCuration, Error, ResearchInput>({
    mutationFn: (input) =>
      api.post<ResearchCuration>(`/api/matters/${encodeURIComponent(matterId)}/research`, input),
    onSuccess: () => {
      // The whole matter prefix, not just the research list: the verdict is
      // an event on the matter's timeline, and any card reading that timeline
      // is stale the moment this returns.
      void client.invalidateQueries({ queryKey: ['matters', matterId] })
    },
  })
}
